import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Star, Trophy, Target, FileText, Gift, LogOut, Upload, ClipboardList, GraduationCap, Camera } from 'lucide-react';
import { getChildTheme } from '../childThemes';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const ChildDashboard = () => {
  const [child, setChild] = useState(null);
  const [theme, setTheme] = useState(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [tempPhoto, setTempPhoto] = useState(null); // Temporary photo before saving
  const [isSaving, setIsSaving] = useState(false); // Prevent multiple saves
  const photoJustSaved = useRef(false); // Track if we just saved a photo
  const [goals, setGoals] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [completedWork, setCompletedWork] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const childUser = localStorage.getItem('childUser');
    const childToken = localStorage.getItem('childToken');
    
    if (!childUser || !childToken) {
      navigate('/child/login');
      return;
    }
    
    // Set API token from childToken (not access_token)
    api.setToken(childToken);
    
    const childData = JSON.parse(childUser);
    setChild(childData);
    const childTheme = getChildTheme(childData.theme || 'purple');
    setTheme(childTheme);
    
    // Load photo from localStorage immediately for instant display
    const cachedPhotoUrl = localStorage.getItem(`child_photo_${childData.id}`);
    if (cachedPhotoUrl && cachedPhotoUrl !== 'null') {
      console.log('Loading cached photo URL:', cachedPhotoUrl);
      setProfilePhoto(cachedPhotoUrl);
    }
    
    loadChildData(childData.id);
  }, [navigate]);

  const loadChildData = async (childId) => {
    try {
      console.log('Loading child data for:', childId);
      const [goalsData, assignmentsData, completedData, subjectsData, childrenData] = await Promise.all([
        api.getGoals(childId),
        api.getAssignments(childId, 'assigned'),
        api.getCompletedWork(childId),
        api.getSubjects(),
        api.getChildren() // Get children to load profile photo
      ]);
      
      console.log('Children data loaded:', childrenData);
      
      // Update photo from DB if different from cached version
      const thisChild = childrenData.find(c => c.id === childId);
      console.log('This child from API:', thisChild);
      
      if (thisChild && thisChild.profile_photo) {
        const dbPhotoUrl = thisChild.profile_photo;
        console.log('Photo URL from DB:', dbPhotoUrl);
        
        // Only update if we don't have a photo, or if DB photo is different
        if (!profilePhoto || (profilePhoto !== dbPhotoUrl && !photoJustSaved.current)) {
          console.log('Updating profile photo from DB:', dbPhotoUrl);
          setProfilePhoto(dbPhotoUrl);
          // Update localStorage cache
          localStorage.setItem(`child_photo_${childId}`, dbPhotoUrl);
        } else {
          console.log('Profile photo already set correctly, no update needed');
        }
      } else {
        console.log('No profile photo found for this child in DB');
      }
      
      // Reset the "just saved" flag after data load
      if (photoJustSaved.current) {
        console.log('Resetting photoJustSaved flag');
        photoJustSaved.current = false;
      }
      
      setGoals(goalsData);
      setAssignments(assignmentsData);
      setCompletedWork(completedData);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Failed to load child data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!child || !theme) return null;

  // Determine semesters based on completed work dates
  const getSemester = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown';
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    
    if (month >= 8 && month <= 12) {
      return `Fall ${year}`;
    } else if (month >= 1 && month <= 5) {
      return `Spring ${year}`;
    } else {
      return `Summer ${year}`;
    }
  };

  // Get unique semesters from completed work
  const semesters = [...new Set(
    completedWork.map(w => getSemester(w.completed_date || w.due_date || w.date_assigned))
  )].filter(s => s !== 'Unknown');
  semesters.sort().reverse();

  // Filter completed work by semester
  const filteredCompletedWork = selectedSemester === 'all' 
    ? completedWork 
    : completedWork.filter(w => getSemester(w.completed_date || w.due_date || w.date_assigned) === selectedSemester);

  // Calculate average grade from filtered completed work
  const calculateAverage = () => {
    if (filteredCompletedWork.length === 0) return 0;
    
    const percentages = filteredCompletedWork.map(work => {
      if (work.points_awarded !== undefined && work.points_possible !== undefined && work.points_possible > 0) {
        return (work.points_awarded / work.points_possible) * 100;
      }
      return work.percentage || 0;
    });
    
    return percentages.reduce((sum, p) => sum + p, 0) / percentages.length;
  };

  const overallAverage = calculateAverage();
  const assignedCount = assignments.filter(a => a.status === 'assigned').length;

  const handleLogout = () => {
    localStorage.removeItem('childUser');
    localStorage.removeItem('childToken');
    // Don't remove 'access_token' - that's for parent login
    api.setToken(null);
    navigate('/child/login');
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const photoData = reader.result;
        setTempPhoto(photoData);
        setShowPhotoDialog(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSavePhoto = async () => {
    if (!tempPhoto || isSaving) {
      console.log('No photo to save or already saving');
      return;
    }
    
    setIsSaving(true);
    console.log('Starting photo save...');
    console.log('Child ID:', child.id);
    console.log('Photo data length:', tempPhoto.length);
    
    try {
      // Save to backend (backend will upload to S3 and return URL)
      console.log('Calling updateChild API...');
      const result = await api.updateChild(child.id, { profile_photo: tempPhoto });
      console.log('Update result:', result);
      
      // Use the S3 URL returned from backend (not the base64)
      const photoUrl = result.profile_photo;
      console.log('Photo URL from backend:', photoUrl);
      
      if (photoUrl) {
        setProfilePhoto(photoUrl);
        const updatedChild = { ...child, profile_photo: photoUrl };
        setChild(updatedChild);
        photoJustSaved.current = true; // Mark that we just saved
        
        // Cache photo URL in localStorage for instant loading on next visit
        localStorage.setItem(`child_photo_${child.id}`, photoUrl);
        console.log('Cached photo URL in localStorage:', photoUrl);
        
        // Force immediate visual update - don't wait for reload
        console.log('Profile photo updated to:', photoUrl);
      }
      
      toast({
        title: 'Photo Saved!',
        description: 'Your profile photo has been saved successfully.'
      });
      setShowPhotoDialog(false);
      setTempPhoto(null);
      
      // Don't call loadChildData here - photo is already set correctly
    } catch (error) {
      console.error('Failed to save photo:', error);
      console.error('Error details:', error.message, error.response);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save profile photo. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelPhoto = () => {
    setTempPhoto(null);
    setShowPhotoDialog(false);
  };

  const handleCameraCapture = () => {
    toast({
      title: 'Camera Feature',
      description: 'Camera capture will be available when backend is connected!'
    });
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.cardBg}`}>
      {/* Child Header - Mobile Responsive */}
      <div className={`bg-gradient-to-r ${theme.headerBg} text-white p-4 md:p-6 shadow-lg`}>
        <div className="max-w-6xl mx-auto">
          {/* Mobile Layout */}
          <div className="flex flex-col gap-4 md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative group">
                  <div
                    className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl font-bold shadow-lg overflow-hidden cursor-pointer relative"
                    style={{ color: child.profileColor }}
                    onClick={() => setShowPhotoDialog(true)}
                  >
                    {profilePhoto ? (
                      <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      child.name.charAt(0)
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
                <div>
                  <h1 className="text-xl font-bold">Hi, {child.name.split(' ')[0]}!</h1>
                  <p className={`text-sm ${theme.accentColor === 'purple-600' ? 'text-purple-100' : 'text-blue-100'}`}>Keep it up!</p>
                </div>
              </div>
              {/* Mobile Logout Button - Always Visible */}
              <Button
                variant="outline"
                size="sm"
                className={`border-white text-white hover:bg-white ${theme.accentColor === 'purple-600' ? 'hover:text-purple-600' : 'hover:text-blue-600'} flex-shrink-0`}
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-300" />
                <span className="text-lg font-bold">{child.tokens}</span>
                <span className="text-xs">tokens</span>
              </div>
              {semesters.length > 1 && (
                <Select value={selectedSemester} onValueChange={setSelectedSemester}>
                  <SelectTrigger className="w-32 bg-white/20 backdrop-blur-sm border-white/30 text-white h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {semesters.map(semester => (
                      <SelectItem key={semester} value={semester}>
                        {semester}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative group">
                <div
                  className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-3xl font-bold shadow-lg overflow-hidden cursor-pointer relative"
                  style={{ color: child.profileColor }}
                  onClick={() => setShowPhotoDialog(true)}
                >
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    child.name.charAt(0)
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold">Hi, {child.name.split(' ')[0]}!</h1>
                <p className={`${theme.accentColor === 'purple-600' ? 'text-purple-100' : 'text-blue-100'}`}>Keep up the great work!</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Semester Selector */}
              {semesters.length > 1 && (
                <Select value={selectedSemester} onValueChange={setSelectedSemester}>
                  <SelectTrigger className="w-40 bg-white/20 backdrop-blur-sm border-white/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {semesters.map(semester => (
                      <SelectItem key={semester} value={semester}>
                        {semester}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 text-yellow-300" />
                  <span className="text-2xl font-bold">{child.tokens}</span>
                  <span className={`${theme.accentColor === 'purple-600' ? 'text-purple-100' : 'text-blue-100'}`}>tokens</span>
                </div>
              </div>
              <Button
                variant="outline"
                className={`border-white text-white hover:bg-white ${theme.accentColor === 'purple-600' ? 'hover:text-purple-600' : 'hover:text-blue-600'}`}
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Action Cards - 6 Colorful Buttons in 3x2 Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Assignments */}
          <Card
            className={`p-10 border-0 shadow-xl bg-gradient-to-br ${theme.buttonPrimary} text-white cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105 relative`}
            onClick={() => navigate('/child/assignments')}
          >
            <ClipboardList className="w-16 h-16 mb-4" />
            <h2 className="text-3xl font-bold mb-2">Assignments</h2>
            <p className={`text-lg ${theme.accentColor === 'purple-600' ? 'text-purple-100' : 'text-blue-100'}`}>
              View and complete your work
            </p>
            {assignedCount > 0 && (
              <div className="absolute top-4 right-4 bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                {assignedCount}
              </div>
            )}
          </Card>

          {/* My Grades */}
          <Card
            className="p-10 border-0 shadow-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105"
            onClick={() => navigate('/child/grades')}
          >
            <GraduationCap className="w-16 h-16 mb-4" />
            <h2 className="text-3xl font-bold mb-2">My Grades</h2>
            <p className="text-lg text-emerald-100">Check your progress</p>
          </Card>

          {/* Rewards Store */}
          <Card
            className="p-10 border-0 shadow-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105"
            onClick={() => navigate('/child/rewards')}
          >
            <Gift className="w-16 h-16 mb-4" />
            <h2 className="text-3xl font-bold mb-2">Rewards Store</h2>
            <p className="text-lg text-amber-100">Spend your {child.tokens} tokens!</p>
          </Card>

          {/* My Goals */}
          <Card
            className="p-10 border-0 shadow-xl bg-gradient-to-br from-teal-500 to-green-500 text-white cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105 relative"
            onClick={() => navigate('/child/goals')}
          >
            <Target className="w-16 h-16 mb-4" />
            <h2 className="text-3xl font-bold mb-2">My Goals</h2>
            <p className="text-lg text-teal-100">Track your achievements</p>
            {goals.length > 0 && (
              <div className="absolute top-4 right-4 bg-white/30 backdrop-blur-sm text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                {goals.length}
              </div>
            )}
          </Card>

          {/* My Collection */}
          <Card
            className="p-10 border-0 shadow-xl bg-gradient-to-br from-rose-500 to-pink-600 text-white cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105"
            onClick={() => navigate('/child/collection')}
          >
            <Trophy className="w-16 h-16 mb-4" />
            <h2 className="text-3xl font-bold mb-2">My Collection</h2>
            <p className="text-lg text-rose-100">View your rewards</p>
          </Card>

          {/* Bonus Submit */}
          <Card
            className={`p-10 border-0 shadow-xl bg-gradient-to-br ${theme.buttonSecondary} text-white cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105`}
            onClick={() => navigate('/child/submit')}
          >
            <Upload className="w-16 h-16 mb-4" />
            <h2 className="text-3xl font-bold mb-2">Bonus Submit</h2>
            <p className={`text-lg ${theme.secondaryColor === 'pink-600' ? 'text-pink-100' : 'text-teal-100'}`}>
              Turn in extra work
            </p>
          </Card>
        </div>
      </div>

      {/* Photo Upload Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={handleCancelPhoto}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
              {tempPhoto ? 'Preview & Save Photo' : 'Update Profile Photo'}
            </DialogTitle>
          </DialogHeader>
          {!tempPhoto ? (
            <div className="space-y-4 mt-4">
              <Button
                onClick={() => document.getElementById('photo-upload').click()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Choose Photo
              </Button>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              {/* Camera button removed - not implemented */}
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="flex justify-center">
                <img 
                  src={tempPhoto} 
                  alt="Preview" 
                  className="w-48 h-48 rounded-full object-cover border-4 border-blue-400"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleCancelPhoto}
                  variant="outline"
                  className="flex-1 py-6 text-slate-600 border-slate-300 hover:bg-slate-50"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePhoto}
                  disabled={isSaving}
                  className="flex-1 py-6 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Photo'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChildDashboard;